import React, { useState } from 'react';
import { Menu, Button, Space, Modal, Form, Input, message, Tooltip, Badge, Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import { getInitialMenuItems, resolveRouteLabel, routes, useMenuItems } from '../../routes';
import type { RouteItem } from '../../routes';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../common/security/AuthContext';
import { canActOnPage, isPathAllowedForRole } from '../../utils/permissions';
import { BellOutlined, GlobalOutlined, DownOutlined, HomeOutlined } from '@ant-design/icons';
import { useAssignedTasks } from '../common/TaskManagement/useAssignedTasks';
import { useTranslation } from 'react-i18next';
import { normalizeLanguage } from '../../i18n/constants';
import './AppHeader.css';

const AppHeader: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, login, logout, page_permissions, permitted_pages, role } = useAuth();
  const { t, i18n } = useTranslation();
  const [loginVisible, setLoginVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const { count: assignedCount } = useAssignedTasks();
  const { headerMenuItems } = useMenuItems();
  const appLang = normalizeLanguage(i18n.resolvedLanguage ?? i18n.language);

  // Üst modül menüsü (CORE, IT, HSE, vb.) — read izni olmayan sayfalar ayıklanır
  const topMenuItems = React.useMemo<MenuProps['items']>(
    () =>
      getInitialMenuItems(
        path =>
          isPathAllowedForRole(path, role) &&
          canActOnPage(page_permissions, permitted_pages, path, 'read')
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n.language, page_permissions, permitted_pages, role]
  );

  const secondaryHeaderItems = React.useMemo<MenuProps['items']>(() => {
    if (!headerMenuItems) return [];
    return headerMenuItems.filter(item => item && (item as any).key !== '/home');
  }, [headerMenuItems]);

  const hasSecondaryMenu = (secondaryHeaderItems?.length ?? 0) > 0;

  // 2. satırda aktif olan (seçili) menü öğesini bul (en uzun prefix eşleşmesi)
  const secondarySelectedKey = React.useMemo(() => {
    // Sadece 2. satırın *üst seviye* öğeleri (dropdown içindekiler değil)
    const keys = (secondaryHeaderItems as any[] | undefined)
      ?.map(it => (it ? String((it as any).key) : ''))
      .filter(Boolean) ?? [];
    let best = keys[0] || '';
    let bestLen = 0;
    for (const k of keys) {
      if (location.pathname.startsWith(k) && k.length > bestLen) {
        best = k;
        bestLen = k.length;
      }
    }
    return best;
  }, [location.pathname, secondaryHeaderItems]);

  // 3. satır: seçili 2. satır öğesinin alt öğeleri (children/grandchildren)
  const tertiary = React.useMemo(() => {
    // Modülü URL'den güvenilir şekilde çıkar (örn. /it/..., /core/...)
    const seg = location.pathname.split('/').filter(Boolean)[0];
    const moduleBase = seg ? `/${seg}` : '/home';
    const section: RouteItem | undefined = Object.values(routes).find((r) => r.path === moduleBase);
    if (!section?.children || !secondarySelectedKey) return { items: [], navMap: {}, selectedKey: '' };

    const selectedNode: RouteItem | undefined = Object.values(section.children).find((c) => c?.path === secondarySelectedKey);
    if (!selectedNode) return { items: [], navMap: {}, selectedKey: '' };

    const isVisible = (n: RouteItem | undefined | null): n is RouteItem =>
      Boolean(n && n.path && (n.label || n.labelKey) && n.showInMenu);

    // Read izni: sayfa öğeleri izinsizse menüde gösterilmez
    const canRead = (path: string) =>
      isPathAllowedForRole(path, role) &&
      canActOnPage(page_permissions, permitted_pages, path, 'read');

    const rawVisibleKids = (n: RouteItem): RouteItem[] => {
      const all: RouteItem[] = [
        ...Object.values(n.children ?? {}),
        ...Object.values(n.grandchildren ?? {}),
      ];
      return all.filter(isVisible);
    };

    // Yaprak (element'li) düğüm read izni ister; grup düğümü en az bir izinli alt öğe ister
    const isPermitted = (n: RouteItem): boolean =>
      (Boolean(n.element) && canRead(n.path)) || rawVisibleKids(n).some(isPermitted);

    const listImmediateVisible = (n: RouteItem): RouteItem[] =>
      rawVisibleKids(n).filter(isPermitted);

    const getFirstNavigableDescendantPath = (n: RouteItem): string | null => {
      const immediate = listImmediateVisible(n);
      for (const c of immediate) {
        // Element'i olan ve izni bulunan ilk sayfa hedeftir
        if (c.element && canRead(c.path)) return c.path;
        // Otherwise try to find first child/grandchild
        const deep = getFirstNavigableDescendantPath(c);
        if (deep) return deep;
      }
      return null;
    };

    const nodes: RouteItem[] = listImmediateVisible(selectedNode);
    const navMap: Record<string, string> = {};

    // 3. satır öğelerini submenu destekli üret (hover ile alt öğeleri göster)
    const toTertiaryMenuItem = (n: RouteItem): NonNullable<MenuProps['items']>[number] => {
      const kids = listImmediateVisible(n);
      const fallback = getFirstNavigableDescendantPath(n);
      navMap[n.path] = (n.element && canRead(n.path) ? n.path : (fallback || n.path));

      if (kids.length > 0) {
        return {
          key: n.path,
          icon: n.icon as React.ReactNode,
          label: resolveRouteLabel(n),
          children: kids.map(k => toTertiaryMenuItem(k)),
        } as NonNullable<MenuProps['items']>[number];
      }

      return {
        key: n.path,
        icon: n.icon as React.ReactNode,
        label: resolveRouteLabel(n),
      } as NonNullable<MenuProps['items']>[number];
    };

    const items: NonNullable<MenuProps['items']>[number][] = nodes.map(toTertiaryMenuItem);

    // Aktif tertiary seçimini belirle: en derin (en uzun prefix) eşleşen key
    const findBestKey = (n: RouteItem, path: string): { key: string; len: number } | null => {
      let best: { key: string; len: number } | null = null;
      if (path.startsWith(n.path)) best = { key: n.path, len: n.path.length };
      const kids = listImmediateVisible(n);
      for (const k of kids) {
        const r = findBestKey(k, path);
        if (r && (!best || r.len > best.len)) best = r;
      }
      return best;
    };

    let bestKey = '';
    let bestLen = 0;
    for (const n of nodes) {
      const r = findBestKey(n, location.pathname);
      if (r && r.len > bestLen) {
        bestKey = r.key;
        bestLen = r.len;
      }
    }

    return { items, navMap, selectedKey: bestKey };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, secondarySelectedKey, i18n.language, page_permissions, permitted_pages, role]);

  const hasTertiaryMenu = (tertiary.items?.length ?? 0) > 0;
  
  // Keep layout/content offset in sync with visible header rows.
  // This prevents "empty stripes" on pages where 2nd/3rd rows are not rendered.
  React.useEffect(() => {
    const top = 40; // .header-top .header-inner height
    const sub = hasSecondaryMenu ? 32 : 0; // .header-sub .header-inner height
    const ter = hasTertiaryMenu ? 28 : 0; // .header-tertiary .header-inner height
    const total = top + sub + ter;
    try {
      document.documentElement.style.setProperty('--app-header-height', `${total}px`);
    } catch {
      // no-op (e.g. SSR)
    }
  }, [hasSecondaryMenu, hasTertiaryMenu]);

  // Aktif rota için en iyi eşleşen üst menü key'ini bul
  const topSelectedKey = React.useMemo(() => {
    const keys: string[] = [];
    const collect = (arr?: NonNullable<MenuProps['items']>) => {
      if (!arr) return;
      for (const it of arr) {
        if (!it) continue;
        const key = (it as any).key as string | undefined;
        if (key) keys.push(String(key));
        const children = (it as any).children as NonNullable<MenuProps['items']> | undefined;
        if (children) collect(children);
      }
    };
    collect(topMenuItems as NonNullable<MenuProps['items']>);
    let best = '/home';
    let bestLen = 0;
    for (const k of keys) {
      if (location.pathname.startsWith(k) && k.length > bestLen) {
        best = k;
        bestLen = k.length;
      }
    }
    return best;
  }, [location.pathname, topMenuItems]);

  const canReadPath = React.useCallback(
    (path: string) =>
      isPathAllowedForRole(path, role) && canActOnPage(page_permissions, permitted_pages, path, 'read'),
    [page_permissions, permitted_pages, role]
  );

  const safeNavigate = React.useCallback(
    (path: string) => {
      // If user can't read the requested path, fall back to module dashboard (if permitted), otherwise Home.
      if (canReadPath(path)) {
        navigate(path);
        return;
      }
      const seg = String(path || '').split('/').filter(Boolean)[0];
      const moduleBase = seg ? `/${seg}` : '';
      const dashboardPath = moduleBase ? `${moduleBase}/dashboard` : '';
      if (dashboardPath && canReadPath(dashboardPath)) {
        navigate(dashboardPath);
        return;
      }
      navigate('/home');
    },
    [canReadPath, navigate]
  );

  // Üst modül menüsü tıklanınca ilgili modülün ilk görünür alt sayfasına git
  const onTopMenuClick: MenuProps['onClick'] = ({ key }) => {
    const keyPath = String(key);
    const section = Object.values(routes).find(r => r.path === keyPath);
    if (section && !section.element) {
      const children = section.children ? Object.values(section.children) : [];
      for (const child of children) {
        const grands = child?.grandchildren ? Object.values(child.grandchildren) : [];
        const firstVisibleGrand = grands.find(
          g => g && g.path && (g.showInMenu || g.label || g.labelKey)
        );
        if (firstVisibleGrand && canReadPath(firstVisibleGrand.path)) {
          safeNavigate(firstVisibleGrand.path);
          return;
        }
        if (child && child.path && (child.showInMenu || child.label || child.labelKey) && canReadPath(child.path)) {
          safeNavigate(child.path);
          return;
        }
      }
      navigate('/home');
      return;
    }
    safeNavigate(keyPath);
  };

  const handleLoginOpen = () => setLoginVisible(true);
  const handleLoginCancel = () => {
    setLoginVisible(false);
    form.resetFields();
  };

  const handleLoginSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      const success = await login(values.username, values.password);
      if (success) {
        setLoginVisible(false);
        form.resetFields();
        message.success(t('auth.loginSuccess'));
      } else {
        message.error(t('auth.loginError'));
      }
    } catch (err) {
      message.error(t('auth.loginError'));
    } finally {
      setLoading(false);
    }
  };


  // coreTodos kept here if other header parts rely on it; count comes from hook

  const userMenu: MenuProps = React.useMemo(() => {
    return {
      items: [
        { key: 'profile', label: t('header.goToProfile') },
        { type: 'divider' },
        { key: 'logout', label: t('header.logout') },
      ],
      onClick: ({ key }) => {
        if (key === 'profile') navigate('/profile');
        if (key === 'logout') logout();
      },
    };
  }, [navigate, logout, t]);


  return (
    <div className="header-container">
      {/* Üst satır: modül menüsü (CORE, IT, HSE, ...) + kullanıcı aksiyonları */}
      <div className="header-top">
        <div className="header-inner">
          <Tooltip title={t('header.goHome')}>
            <Button
              type="text"
              icon={<HomeOutlined />}
              onClick={() => navigate('/home')}
              style={{ color: '#fff', marginRight: 8 }}
            />
          </Tooltip>
          <Menu
            mode="horizontal"
            className="modern-menu"
            items={topMenuItems}
            selectedKeys={[topSelectedKey]}
            onClick={onTopMenuClick}
            style={{ flex: 1, minWidth: 0, border: 'none', background: 'transparent' }}
          />
          <div className="header-actions">
            <Tooltip title={t('header.language')}>
              <Dropdown
                trigger={['click']}
                menu={{
                  items: [
                    { key: 'en', label: 'English' },
                    { key: 'tr', label: 'Türkçe' },
                  ],
                  onClick: ({ key }) => {
                    i18n.changeLanguage(String(key));
                  },
                }}
              >
                <Button
                  type="text"
                  icon={<GlobalOutlined />}
                  style={{ color: '#fff' }}
                >
                  {appLang.toUpperCase()}
                </Button>
              </Dropdown>
            </Tooltip>
            {!user ? (
              <Button type="primary" onClick={handleLoginOpen}>{t('header.login')}</Button>
            ) : (
              <Space>
                <Tooltip title={t('header.assignedTasks')}>
                  <Badge count={assignedCount || 0} overflowCount={99} offset={[0,0]} style={{ backgroundColor: '#faad14' }}>
                    <BellOutlined
                      style={{ fontSize: 18, color: '#faad14', cursor: 'pointer' }}
                      onClick={() => navigate('/tasks')}
                    />
                  </Badge>
                </Tooltip>
                <Dropdown trigger={['click']} menu={userMenu}>
                  <Button
                    type="text"
                    style={{ fontWeight: 500, color: '#ffffff' }}
                    icon={<DownOutlined />}
                  >
                    {user}
                  </Button>
                </Dropdown>
              </Space>
            )}
          </div>
        </div>
      </div>

      {/* Alt satır: bölüm içi alt menü (ör. QA/QC NDT altında Reports / Pipe Inspections) */}
      {hasSecondaryMenu && (
        <div className="header-sub">
          <div className="header-inner">
            <Menu
              mode="horizontal"
              selectedKeys={secondarySelectedKey ? [secondarySelectedKey] : [location.pathname]}
              items={secondaryHeaderItems}
              onClick={({ key }) => navigate(String(key))}
              style={{ border: 'none', background: 'transparent', flex: 1, minWidth: 0 }}
              className="section-menu"
            />
          </div>
        </div>
      )}

      {/* 3. satır: 2. satırda seçili menünün alt öğeleri */}
      {hasTertiaryMenu && (
        <div className="header-tertiary">
          <div className="header-inner">
            <Menu
              mode="horizontal"
              selectedKeys={tertiary.selectedKey ? [tertiary.selectedKey] : []}
              items={tertiary.items}
              onClick={({ key }) => navigate(String(tertiary.navMap[String(key)] || key))}
              style={{ border: 'none', background: 'transparent', flex: 1, minWidth: 0 }}
              className="tertiary-menu"
            />
          </div>
        </div>
      )}

      <Modal
        title={t('auth.loginTitle')}
        open={loginVisible}
        onCancel={handleLoginCancel}
        onOk={handleLoginSubmit}
        confirmLoading={loading}
        okText={t('header.login')}
        destroyOnClose
      >
        <Form form={form} layout="vertical" name="login_form">
          <Form.Item
            name="username"
            label={t('auth.username')}
            rules={[{ required: true, message: t('auth.usernameRequired') }]}
          >
            <Input autoFocus />
          </Form.Item>
          <Form.Item
            name="password"
            label={t('auth.password')}
            rules={[{ required: true, message: t('auth.passwordRequired') }]}
          >
            <Input.Password />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AppHeader;